// src/DimensionnementChauffeEauModal.js
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

import { supabase } from './supabaseClient';

const getProductImage = (ref) => `https://devis.takoussane.com/images/${ref}.png`;

const MODELES = [
  { key: 'CP',  label: 'Collecteur Plat',          prefix: 'CHAUFFE-CP-',  suffix: '' },
  { key: 'BF',  label: 'Collecteur Plat Blue Film', prefix: 'CHAUFFE-BF-',  suffix: '' },
  { key: 'TV',  label: 'Tubes sous Vide',           prefix: 'CHAUFFE-TV-',  suffix: '' },
];

// Prix collecteur plat de base (fallback si Supabase indisponible)
const PRIX_BASE = {
  'CHAUFFE-100L': 479500, 'CHAUFFE-200L': 579200,
  'CHAUFFE-250L': 642200, 'CHAUFFE-360L': 879044,
};

const CAPACITES = [
  { litres: 50,  foyer: '1-2',   installation: 44750  },
  { litres: 100, foyer: '2-3',   installation: 55310  },
  { litres: 150, foyer: '4-5',   installation: 64500  },
  { litres: 200, foyer: '6-8',   installation: 74800  },
  { litres: 250, foyer: '8-10',  installation: 82300  },
  { litres: 300, foyer: '10-12', installation: 89750  },
  { litres: 360, foyer: '12-14', installation: 97500  },
];

const REGIONS = [
  { key: 'Dakar',       label: 'Dakar / Rufisque / Banlieue', deplacement: 0     },
  { key: 'Thies',       label: 'Thi\u00e8s',                  deplacement: 8000  },
  { key: 'Diourbel',    label: 'Diourbel',                    deplacement: 15000 },
  { key: 'Fatick',      label: 'Fatick',                      deplacement: 15000 },
  { key: 'Kaolack',     label: 'Kaolack',                     deplacement: 20000 },
  { key: 'Kaffrine',    label: 'Kaffrine',                    deplacement: 25000 },
  { key: 'Louga',       label: 'Louga',                       deplacement: 25000 },
  { key: 'Saint-Louis', label: 'Saint-Louis',                 deplacement: 35000 },
  { key: 'Matam',       label: 'Matam',                       deplacement: 45000 },
  { key: 'Tambacounda', label: 'Tambacounda',                 deplacement: 40000 },
  { key: 'Kolda',       label: 'Kolda',                       deplacement: 45000 },
  { key: 'Sedhiou',     label: 'S\u00e9dhiou',                deplacement: 45000 },
  { key: 'Ziguinchor',  label: 'Ziguinchor',                  deplacement: 50000 },
  { key: 'Kedougou',    label: 'K\u00e9dougou',               deplacement: 55000 },
];

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const DimensionnementChauffeEauModal = ({ onClose, onSelect }) => {
  const [modele, setModele] = useState(MODELES[0]);
  const [capacite, setCapacite] = useState(CAPACITES[2]);

  const [showIA, setShowIA] = useState(false);
  const [promptIA, setPromptIA] = useState('');
  const [loadingIA, setLoadingIA] = useState(false); // 150L par défaut
  const analyserIA = async () => {
    if (!promptIA.trim()) return;
    setLoadingIA(true);
    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer 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' },
        body: JSON.stringify({ model: 'llama-3.3-70b-versatile', messages: [{ role: 'system', content: 'Tu es un expert en chauffe-eau solaire. Analyse la description et retourne UNIQUEMENT un JSON: {"litres": number}. Litres recommandés: 50L=1-2 personnes, 100L=2-3, 150L=4-5, 200L=6-8, 300L=8-12.' }, { role: 'user', content: promptIA }], temperature: 0.1, max_tokens: 100 })
      });
      const data = await res.json();
      let content = data.choices?.[0]?.message?.content || '{}';
      content = content.replace(/```json/g,'').replace(/```/g,'').trim();
      const parsed = JSON.parse(content);
      if (parsed.litres) { const cap = CAPACITES.find(c => c.litres >= parsed.litres) || CAPACITES[CAPACITES.length-1]; setCapacite(cap); }
      setShowIA(false); setPromptIA('');
    } catch(e) { console.error('IA chauffe:', e); }
    setLoadingIA(false);
  };

  const [region, setRegion] = useState(REGIONS[0]);
  const [prixDB, setPrixDB] = useState({});
  const [regionsDB, setRegionsDB] = useState(REGIONS);

  useEffect(() => {
    // Charger prix depuis Supabase
    supabase.from('catalogue_produits')
      .select('ref, prix')
      .eq('actif', true)
      .eq('categorie', 'Chauffe-eaux Solaires')
      .then(({ data }) => {
        if (data && data.length > 0) {
          const map = {};
          data.forEach(p => { map[p.ref] = p.prix; });
          setPrixDB(map);
        }
      });

    // Charger déplacements depuis Supabase
    supabase.from('catalogue_produits').select('ref,prix').like('ref', 'DEPL-%').eq('actif', true)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const map = {};
          data.forEach(d => { map[d.ref] = d.prix; });
          setRegionsDB([
            { key: 'Dakar',       label: 'Dakar / Rufisque / Banlieue', deplacement: map['DEPL-DAKAR']       ?? 0     },
            { key: 'Thies',       label: 'Thi\u00e8s',                  deplacement: map['DEPL-THIES']       ?? 8000  },
            { key: 'Diourbel',    label: 'Diourbel',                    deplacement: map['DEPL-DIOURBEL']    ?? 15000 },
            { key: 'Fatick',      label: 'Fatick',                      deplacement: map['DEPL-FATICK']      ?? 15000 },
            { key: 'Kaolack',     label: 'Kaolack',                     deplacement: map['DEPL-KAOLACK']     ?? 20000 },
            { key: 'Kaffrine',    label: 'Kaffrine',                    deplacement: map['DEPL-KAFFRINE']    ?? 25000 },
            { key: 'Louga',       label: 'Louga',                       deplacement: map['DEPL-LOUGA']       ?? 25000 },
            { key: 'Saint-Louis', label: 'Saint-Louis',                 deplacement: map['DEPL-SAINTLOUIS']  ?? 35000 },
            { key: 'Matam',       label: 'Matam',                       deplacement: map['DEPL-MATAM']       ?? 45000 },
            { key: 'Tambacounda', label: 'Tambacounda',                 deplacement: map['DEPL-TAMBACOUNDA'] ?? 40000 },
            { key: 'Kolda',       label: 'Kolda',                       deplacement: map['DEPL-KOLDA']       ?? 45000 },
            { key: 'Sedhiou',     label: 'S\u00e9dhiou',                deplacement: map['DEPL-SEDHIOU']     ?? 45000 },
            { key: 'Ziguinchor',  label: 'Ziguinchor',                  deplacement: map['DEPL-ZIGUINCHOR']  ?? 50000 },
            { key: 'Kedougou',    label: 'K\u00e9dougou',               deplacement: map['DEPL-KEDOUGOU']    ?? 55000 },
          ]);
        }
      });
  }, []);

  // Calculer le prix du chauffe-eau
  const getRef = () => {
    if (capacite.litres === 100 && modele.key === 'CP') return 'CHAUFFE-CP-100L';
    if (capacite.litres === 200 && modele.key === 'CP') return 'CHAUFFE-CP-200L';
    if (capacite.litres === 250 && modele.key === 'CP') return 'CHAUFFE-CP-250L';
    if (capacite.litres === 360 && modele.key === 'CP') return 'CHAUFFE-CP-360L';
    return modele.prefix + capacite.litres + 'L';
  };

  const ref = getRef();
  const prixChauffe = prixDB[ref] || PRIX_BASE[ref] || 0;
  const prixInstall = capacite.installation;
  const prixDepl = region.deplacement;
  const total = prixChauffe + prixInstall + (prixDepl > 0 ? prixDepl : 0);

  const nomProduit = 'SunXellence SUNNYBOILER ' + modele.label + ' ' + capacite.litres + 'L';
  const descChauffe = nomProduit + '\nFoyer : ' + capacite.foyer + ' personnes/jour'
    + '\nR\u00e9servoir inox SUS304-2B | Isolation mousse PU haute densit\u00e9 | Pression 6 bars | Thermosyphon sans pompe';

  const handleInserer = () => {
    const items = [];

    // Titre
    items.push({
      _titre: true,
      description: 'KIT CHAUFFE-EAU SOLAIRE TAKOUSSANE ENERGY | SunXellence SUNNYBOILER '
        + modele.label.toUpperCase() + ' ' + capacite.litres + 'L'
        + ' | Foyer : ' + capacite.foyer + ' pers/j | Site : ' + region.label,
      quantity: 1,
      unitPrice: 0,
    });

    // Chauffe-eau
    items.push({
      description: descChauffe,
      quantity: 1,
      unitPrice: prixChauffe,
      image: getProductImage(ref),
    });

    // Installation
    items.push({
      description: 'Forfait pose et installation Chauffe-Eau Solaire ' + capacite.litres + 'L\nMontage m\u00e9canique et fixation \u00e0 la terrasse\nNB : Raccordements hydrauliques et mise en service non inclus (plombier partenaire sur demande)',
      quantity: 1,
      unitPrice: prixInstall,
      image: getProductImage('PRESTA-INSTALL-PV'),
    });

    // Déplacement si hors Dakar
    if (prixDepl > 0) {
      items.push({
        description: 'D\u00e9placement — ' + region.label,
        quantity: 1,
        unitPrice: prixDepl,
      });
    }

    onSelect(items);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 80, padding: '16px',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', width: '100%', maxWidth: '560px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>
              Dimensionnement Chauffe-eau Solaire — SunXellence SUNNYBOILER
            </div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12px', marginTop: '4px' }}>
              Sélectionnez modèle, capacité et région
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={() => setShowIA(!showIA)} style={{ background: showIA ? 'linear-gradient(135deg,#7c3aed,#a855f7)' : 'white', border: 'none', borderRadius: '8px', padding: '6px 14px', color: showIA ? 'white' : '#7c3aed', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>✨ IA</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '4px' }}><X size={20} /></button>
          </div>
        </div>

        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* Type de modèle */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
              Type de collecteur
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {MODELES.map(m => (
                <button key={m.key} onClick={() => setModele(m)}
                  style={{
                    padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                    border: '2px solid ' + (modele.key === m.key ? '#ea580c' : '#e5e7eb'),
                    background: modele.key === m.key ? '#fff7ed' : 'white',
                    color: modele.key === m.key ? '#ea580c' : '#6b7280',
                    cursor: 'pointer',
                  }}>
                  {m.key === 'BF' ? '⭐ ' : ''}{m.label}
                  {m.key === 'CP' && <span style={{ fontSize: '10px', marginLeft: '4px', color: '#10b981' }}>Vedette</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Capacité selon foyer */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
          {showIA && (
            <div style={{ background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)', border: '1px solid #c4b5fd', borderRadius: '12px', padding: '16px', marginBottom: '8px' }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#5b21b6', marginBottom: '4px' }}>✨ Décris le projet, l’IA s’occupe du reste</div>
              <div style={{ fontSize: '11px', color: '#7c3aed', marginBottom: '10px' }}>Ex: famille de 5 personnes à Ziguinchor, usage quotidien matin et soir</div>
              <textarea value={promptIA} onChange={e => setPromptIA(e.target.value)} rows={2}
                placeholder="Décris le besoin en eau chaude..."
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '8px', fontSize: '13px', outline: 'none', background: 'white', resize: 'none', fontFamily: 'inherit' }} />
              <button onClick={analyserIA} disabled={loadingIA || !promptIA.trim()}
                style={{ marginTop: '8px', width: '100%', padding: '9px', background: loadingIA ? '#a78bfa' : 'linear-gradient(135deg,#7c3aed,#a855f7)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: 700, cursor: loadingIA ? 'wait' : 'pointer' }}>
                {loadingIA ? '⏳ Analyse...' : '✨ Analyser'}
              </button>
            </div>
          )}
              Capacité selon le foyer
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
              {CAPACITES.map(c => (
                <button key={c.litres} onClick={() => setCapacite(c)}
                  style={{
                    padding: '10px 6px', borderRadius: '8px', textAlign: 'center',
                    border: '2px solid ' + (capacite.litres === c.litres ? '#ea580c' : '#e5e7eb'),
                    background: capacite.litres === c.litres ? '#fff7ed' : 'white',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: capacite.litres === c.litres ? '#ea580c' : '#1f2937' }}>
                    {c.litres}L
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                    {c.foyer} pers/j
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Région */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
              Région d'intervention
            </div>
            <select
              value={region.key}
              onChange={e => setRegion(regionsDB.find(r => r.key === e.target.value))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }}>
              {regionsDB.map(r => (
                <option key={r.key} value={r.key}>
                  {r.label}{r.deplacement > 0 ? ' — Dép. : ' + fmt(r.deplacement) + ' FCFA' : ' — Déplacement inclus'}
                </option>
              ))}
            </select>
          </div>

          {/* Récapitulatif */}
          <div style={{ background: '#fff7ed', borderRadius: '10px', padding: '14px 16px', border: '1px solid #fed7aa' }}>
            <div style={{ fontWeight: 700, fontSize: '13px', color: '#ea580c', marginBottom: '10px' }}>
              Récapitulatif — {capacite.litres}L {modele.label}
            </div>
            {[
              [nomProduit, fmt(prixChauffe) + ' FCFA'],
              ['Forfait installation', fmt(prixInstall) + ' FCFA'],
              ...(prixDepl > 0 ? [['Déplacement — ' + region.label, fmt(prixDepl) + ' FCFA']] : [['Déplacement', 'Inclus']]),
            ].map(([label, val], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span style={{ color: '#374151' }}>{label}</span>
                <span style={{ fontWeight: 600, color: '#ea580c' }}>{val}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid #fed7aa', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, color: '#1f2937' }}>Total kit</span>
              <span style={{ fontWeight: 700, fontSize: '15px', color: '#ea580c' }}>{fmt(total)} FCFA</span>
            </div>
          </div>

          {/* Bouton insérer */}
          <button onClick={handleInserer}
            style={{
              background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
              color: 'white', border: 'none', borderRadius: '10px',
              padding: '14px', fontSize: '14px', fontWeight: 700,
              cursor: 'pointer', width: '100%',
            }}>
            🌡️ Insérer le chauffe-eau dans le devis
          </button>
        </div>
      </div>
    </div>
  );
};

export default DimensionnementChauffeEauModal;
